// Libs
import classNames from 'classnames/bind';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Box, Button, Grid2, IconButton, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLoadScript } from '@react-google-maps/api';
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
// Components, Layouts, Pages
import { BaseInput, BaseMap, BaseSelect } from '~/components/common';
import { ModalEditAddress } from '~/components/module/job';
// Others
import {
  BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_GG_MAP_LOAD_SCRIPT_LIB,
  DEFAULT_LIMIT_1000,
  DEFAULT_NUMBER_ZERO,
  EMPTY_STRING,
} from '~/utils/constants/common';
import { IQueryParamBase } from '~/utils/interface/common';
import { googleMapApiKey } from '~/utils/constants/env';
import {
  IAddressCustomerOption,
  IAddressCustomerRes,
  ICustomer,
  ICustomerOption,
} from '~/utils/interface/customer';
import { useAppDispatch } from '~/utils/redux/hooks';
import { getCustomerList } from '~/thunks/customer/customerThunk';
import { generateGuid, getFullAddress, getFullName } from '~/utils/helper/common';
import useDebounce from '~/utils/hooks/useDebounce';
import { IAddressJob, IFormJob } from '~/utils/interface/job';
import { TypeTabCreateJobEnum } from '~/utils/enum/common';
// Styles, images, icons
import styles from './CreateJobCustomer.module.scss';
import { icons } from '~/assets';

type Props = {
  onEdit?: (data: IFormJob, type: string) => void;
  onCancelEdit: () => void;
};

const cx = classNames.bind(styles);

const CreateJobCustomer = (props: Props) => {
  //#region Destructuring Props
  const { onEdit, onCancelEdit } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/job']);
  const dispatch = useAppDispatch();
  const { id: jobId } = useParams();
  const [searchParams] = useSearchParams();
  const customerId = searchParams.get('customerId');
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: googleMapApiKey ?? EMPTY_STRING,
    libraries: DEFAULT_GG_MAP_LOAD_SCRIPT_LIB,
  });

  const {
    control,
    setValue,
    watch,
    getValues,
    formState: { errors, isDirty },
  } = useFormContext<IFormJob>();

  const latitude = watch('latitude');
  const longitude = watch('longitude');
  const clientId = useWatch({ control, name: 'clientId' });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoadingCustomer, setIsLoadingCustomer] = useState<boolean>(false);
  const [customerOptions, setCustomerOptions] = useState<ICustomerOption[]>([]);
  const currentPageCustomer = BASE_PAGINATION_DEFAULT_CURRENT_PAGE;
  const [customerSearchKey, setCustomerSearchKey] = useState<string>(EMPTY_STRING);
  const debounceCustomerSearchKey = useDebounce(customerSearchKey, DEFAULT_DEBOUNCE_DELAY_TIME);
  const [isDisplayModalEditAddress, setIsDisplayModalEditAddress] = useState<boolean>(false);
  const [addressSelected, setAddressSelected] = useState<IAddressCustomerOption | null>(null);

  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    const params: IQueryParamBase = {
      page: currentPageCustomer,
      limit: DEFAULT_LIMIT_1000,
      ...(debounceCustomerSearchKey && { textSearch: debounceCustomerSearchKey }),
    };

    handleGetCustomerList(params);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPageCustomer, debounceCustomerSearchKey]);

  useEffect(() => {
    if (!clientId) return;
    if (jobId) return;

    const findCustomer = customerOptions?.find((item) => item?.value === clientId);

    convertAddressToOptions(findCustomer?.addresses || []);

    setValue('address', findCustomer?.addresses?.[DEFAULT_NUMBER_ZERO]?.address || EMPTY_STRING, {
      shouldValidate: true,
    });
    setValue('city', findCustomer?.addresses?.[DEFAULT_NUMBER_ZERO]?.city || EMPTY_STRING, {
      shouldValidate: true,
    });
    setValue('state', findCustomer?.addresses?.[DEFAULT_NUMBER_ZERO]?.state || EMPTY_STRING, {
      shouldValidate: true,
    });
    setValue('zipCode', findCustomer?.addresses?.[DEFAULT_NUMBER_ZERO]?.zipCode || EMPTY_STRING, {
      shouldValidate: true,
    });
    setValue('country', findCustomer?.addresses?.[DEFAULT_NUMBER_ZERO]?.country || EMPTY_STRING, {
      shouldValidate: true,
    });

    if (findCustomer && findCustomer?.addresses?.[DEFAULT_NUMBER_ZERO]) {
      const firstAddress = findCustomer.addresses[DEFAULT_NUMBER_ZERO];

      const converted = {
        id: firstAddress.id,
        label: getFullAddress({ ...firstAddress }),
        value: firstAddress.address || EMPTY_STRING,
        address: firstAddress.address || EMPTY_STRING,
        city: firstAddress.city || EMPTY_STRING,
        state: firstAddress.state || EMPTY_STRING,
        zipCode: firstAddress.zipCode || EMPTY_STRING,
        country: firstAddress.country || EMPTY_STRING,
        address2: firstAddress.address2 || EMPTY_STRING,
      };

      setAddressSelected(converted);
      return;
    }
    setAddressSelected(null);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, customerOptions]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleConvertCustomerOptions = (customerList: ICustomer[]) => {
    const options: ICustomerOption[] = customerList.map((customer) => ({
      value: customer.id,
      label: getFullName({ firstName: customer.firstName, lastName: customer.lastName }),
      addresses: customer?.addresses,
    }));

    setCustomerOptions((prev) =>
      currentPageCustomer === BASE_PAGINATION_DEFAULT_CURRENT_PAGE ? options : prev.concat(options)
    );
  };

  const handleGetCustomerList = (params: IQueryParamBase) => {
    setIsLoadingCustomer(true);

    dispatch(getCustomerList(params))
      .unwrap()
      .then((resp) => {
        const { responses } = resp.data;
        if (!responses) {
          return;
        }

        handleConvertCustomerOptions(responses);
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoadingCustomer(false);
      });
  };

  const handleSearchCustomerChange = (searchKey: string) => {
    setCustomerSearchKey(searchKey);
  };

  const handleClickEditAddress = () => {
    setIsDisplayModalEditAddress(true);
  };

  const handleCloseModalEditAddress = () => {
    setIsDisplayModalEditAddress(false);
  };

  const convertAddressToOptions = (address: IAddressCustomerRes[]) => {
    return address?.map((address) => ({
        id: address.id || EMPTY_STRING,
        label: getFullAddress({ ...address }),
        value: address.address || EMPTY_STRING,
        address: address.address || EMPTY_STRING,
        city: address.city || EMPTY_STRING,
        state: address.state || EMPTY_STRING,
        zipCode: address.zipCode || EMPTY_STRING,
        country: address.country || EMPTY_STRING,
        address2: address.address2 || EMPTY_STRING,
      })) || [];
  };

  const updateAddressLabel = (id: string, newLabel: IAddressCustomerRes) => {
    const findCustomer = customerOptions?.find((item) => item?.value === clientId);
    const existingAddresses = findCustomer?.addresses || [];

    const isDuplicateAddress = existingAddresses.some(
      (item) =>
        (item.address || EMPTY_STRING) === newLabel.address &&
        (item.city || EMPTY_STRING) === newLabel.city &&
        (item.state || EMPTY_STRING) === newLabel.state &&
        (item.zipCode || EMPTY_STRING) === newLabel.zipCode &&
        (item.country || EMPTY_STRING) === newLabel.country
    );

    const updateAddress = existingAddresses?.map((item) => {
      if (item?.id === id) {
        return {
          ...item,
          label: getFullAddress({ ...newLabel }),
          value: newLabel.address || EMPTY_STRING,
          address2: newLabel.address2 || EMPTY_STRING,
        };
      }
      return item;
    });

    const newAddressToAdd = !isDuplicateAddress ? [{ ...newLabel, id: generateGuid() }] : [];

    convertAddressToOptions([...newAddressToAdd, ...updateAddress]);
  };

  const handleChangeAddress = (address: IAddressJob) => {
    setValue('address', address.address || EMPTY_STRING, { shouldValidate: true, shouldDirty: true });
    setValue('address2', address.address2 || EMPTY_STRING, { shouldValidate: true, shouldDirty: true });
    setValue('city', address.city || EMPTY_STRING, { shouldValidate: true, shouldDirty: true });
    setValue('state', address.state || EMPTY_STRING, { shouldValidate: true, shouldDirty: true });
    setValue('zipCode', address.zipCode || EMPTY_STRING, { shouldValidate: true, shouldDirty: true });
    setValue('country', address.country || EMPTY_STRING, { shouldValidate: true, shouldDirty: true });

    const newOption: IAddressCustomerOption = {
      ...address,
      label: getFullAddress({ ...address }),
      value: address.address || EMPTY_STRING,
    };

    setAddressSelected(newOption);

    updateAddressLabel(address?.id || EMPTY_STRING, address as IAddressCustomerRes);
  };

  const handleSubmitFormCustomer = () => {
    const dataForm = getValues();
    const dataBody: IFormJob = {
      name: dataForm?.name || EMPTY_STRING,
      clientId: dataForm?.clientId || EMPTY_STRING,
      address: dataForm?.address || EMPTY_STRING,
      address2: dataForm?.address2 || EMPTY_STRING,
      city: dataForm?.city || EMPTY_STRING,
      state: dataForm?.state || EMPTY_STRING,
      zipCode: dataForm?.zipCode || EMPTY_STRING,
      country: dataForm?.country || EMPTY_STRING,
    };

    onEdit && onEdit(dataBody, TypeTabCreateJobEnum.CUSTOMER);
  };
  //#endregion Handle Function

  return (
    <Box component='div' className={cx('container')} id='createJobCustomerTabComponent'>
      <Grid2 container rowSpacing={'24px'} columnSpacing={'12px'}>
        <Grid2 size={12}>
          <Controller
            name={`name`}
            control={control}
            render={({ field: { value, onChange } }) => (
              <BaseInput
                label={t('field_customer_job_name')}
                value={value || EMPTY_STRING}
                onChange={onChange}
                errorMessage={errors?.name?.message}
                required
              />
            )}
          />
        </Grid2>

        <Grid2 size={12}>
          <Controller
            name={`clientId`}
            control={control}
            render={({ field: { value, onChange } }) => (
              <BaseSelect
                label={t('field_customer_name')}
                value={value || EMPTY_STRING}
                onChange={(optionSelected: ICustomerOption) => {
                  onChange(optionSelected.value);
                  setValue(
                    'address',
                    optionSelected?.addresses?.[DEFAULT_NUMBER_ZERO]?.address || EMPTY_STRING
                  );
                }}
                options={customerOptions || []}
                isLoading={isLoadingCustomer}
                onSearchChange={handleSearchCustomerChange}
                errorMessage={errors?.clientId?.message}
                required
                disabled={!!customerId || !!jobId}
              />
            )}
          />
        </Grid2>

        <Grid2 container size={12} rowSpacing={'24px'} columnSpacing={'12px'}>
          <Grid2 size='grow'>
            <Controller
              name={`address`}
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseInput
                  label={t('field_customer_address')}
                  value={value || EMPTY_STRING}
                  onChange={onChange}
                  errorMessage={errors?.address?.message}
                  disabled
                  required
                />
              )}
            />
          </Grid2>

          <Grid2 size='auto' className='flex'>
            <IconButton
              className={cx('closeField')}
              onClick={() => handleClickEditAddress()}
              disabled={!clientId || !!jobId}
            >
              <img src={icons.tableIconActionEdit} alt={t('common_alt_img', { ns: 'common/base' })} />
            </IconButton>
          </Grid2>
        </Grid2>

        <Grid2 size={12}>
          <Box component={'div'} className={cx('map')}>
            {isLoaded && (
              <BaseMap
                {...(latitude &&
                  longitude && {
                    position: {
                      lat: latitude as number,
                      lng: longitude as number,
                    },
                  })}
              />
            )}
          </Box>
        </Grid2>

        {jobId && (
          <Stack
            direction={'row'}
            alignItems={'center'}
            justifyContent={'flex-end'}
            component={'div'}
            spacing={'12px'}
            className={cx('formFooter')}
          >
            <Button variant='outlined' onClick={onCancelEdit}>
              {t('common_action_cancel', { ns: 'common/base' })}
            </Button>

            <Button variant='contained' type='button' onClick={handleSubmitFormCustomer} disabled={!isDirty}>
              {t('common_action_save', { ns: 'common/base' })}
            </Button>
          </Stack>
        )}
      </Grid2>

      {isDisplayModalEditAddress && (
        <ModalEditAddress
          onClose={handleCloseModalEditAddress}
          data={addressSelected}
          onChange={handleChangeAddress}
        />
      )}
    </Box>
  );
};

export default CreateJobCustomer;
